import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useCallback, useMemo, useState } from 'react'

import type { NodeOverlay, PageGraphSource } from './canvasTypes'
import { useIsolationController } from './IsolationControllerContext'
import type {
  IsolationFocus,
  IsolationNodeRef,
  IsolationStopBadge,
} from './isolationMarkers'
import { selectedStopBadges } from './isolationMarkup'
import {
  findIsolationStop,
  nodesOnPages,
  summarizeIsolationNode,
} from './isolationSelection'
import { useIsGraphLocked } from './useIsGraphLocked'

const noStops: IsolationStopBadge[] = []
const noHighlights: IsolationNodeRef[] = []

export function usePageIsolation({
  pageGraphs,
  isActiveTab,
}: {
  pageGraphs: PageGraphSource[]
  isActiveTab: boolean
}): {
  isolating: boolean
  stops: IsolationStopBadge[]
  highlights: IsolationNodeRef[]
  focus?: IsolationFocus
  onStopFocused: (nodeId: string) => void
  onSelectNode?: (overlay: NodeOverlay) => void
} {
  const controller = useIsolationController()
  const locked = useIsGraphLocked()
  const { isPipelineConfigScopeLoading } = usePipelineConfig()
  const stops = useMemo(
    () =>
      controller
        ? selectedStopBadges(controller.selection, pageGraphs)
        : noStops,
    [controller, pageGraphs]
  )
  const highlights = useMemo(
    () =>
      controller?.highlightedNodeIds
        ? nodesOnPages(controller.highlightedNodeIds, pageGraphs)
        : noHighlights,
    [controller, pageGraphs]
  )
  const [focus, setFocus] = useState<IsolationFocus>()
  const onStopFocused = useCallback((nodeId: string): void => {
    setFocus((current) => ({ nodeId, count: (current?.count ?? 0) + 1 }))
  }, [])
  const onToggleNode = controller?.onToggleNode
  const selectNode = useCallback(
    ({ node }: NodeOverlay): void => {
      const stop = findIsolationStop(pageGraphs, node.id)
      if (stop) onToggleNode?.(summarizeIsolationNode(stop))
    },
    [onToggleNode, pageGraphs]
  )
  const allowed =
    !locked &&
    !isPipelineConfigScopeLoading &&
    isActiveTab &&
    pageGraphs.length > 0
  if (!controller || !allowed) {
    if (focus) setFocus(undefined)
    return {
      isolating: false,
      stops: noStops,
      highlights: noHighlights,
      onStopFocused,
    }
  }
  return {
    isolating: true,
    stops,
    highlights,
    focus,
    onStopFocused,
    onSelectNode: selectNode,
  }
}
